import { createContext, useContext, useState, useEffect } from "react";
import { sendUserAttribution } from "@/lib/attribution";
import { trackStandard } from "@/lib/metaPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  trialInfo: null,
  login: () => {},
  logout: () => {},
  checkAuth: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [trialInfo, setTrialInfo] = useState(null);

  const checkAuth = async () => {
    try {
      // Get session token from localStorage as fallback
      const sessionToken = localStorage.getItem("session_token");
      const headers = {};
      if (sessionToken) {
        headers["Authorization"] = `Bearer ${sessionToken}`;
      }
      
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        sendUserAttribution();
        try {
          if (userData.user_id && userData.created_at &&
              Date.now() - new Date(userData.created_at).getTime() < 10 * 60 * 1000) {
            trackStandard("CompleteRegistration", { content_name: "student_registration" }, `reg_${userData.user_id}`);
          }
        } catch {}
        // Set trial info from user data
        setTrialInfo({
          trialEndsAt: userData.trial_ends_at,
          subscriptionStatus: userData.subscription_status || "trial",
          daysRemaining: userData.days_remaining || 0,
          isTrialActive: userData.is_trial_active || false
        });
      } else {
        setUser(null);
        setTrialInfo(null);
        // Clear invalid token
        localStorage.removeItem("session_token");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
      setTrialInfo(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || 
        window.location.search?.includes('session_id=') ||
        window.location.pathname === '/auth/callback') {
      setIsLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const login = () => {
    // Preserve deep-link: remember where the user was before OAuth
    try {
      const p = window.location.pathname;
      if (p && p !== "/" && !p.startsWith("/dashboard") && !p.startsWith("/login")) {
        sessionStorage.setItem("cm_post_login_redirect", p + window.location.search);
      } else {
        sessionStorage.removeItem("cm_post_login_redirect");
      }
    } catch { /* ignore */ }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use /dashboard as redirect - session_id will be in URL fragment
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      const sessionToken = localStorage.getItem("session_token");
      const headers = {};
      if (sessionToken) {
        headers["Authorization"] = `Bearer ${sessionToken}`;
      }
      
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers,
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      setTrialInfo(null);
      localStorage.removeItem("session_token");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        trialInfo,
        login,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
